import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  TeacherOption,
  TeachingRangeOption,
} from 'src/app/services/models/subjects/subject.model';
import { SharedModule } from 'src/shared/shared.module';
import {
  TeachingTimeValue,
  calculateRegularTeacherWorkloadHours,
} from '../../domain/subject-management-create-teacher.builder';
import {
  buildAvailableTeacherTeachingRangeOptions,
  parseTeachingRangeIsoDate,
} from '../../domain/subject-management-create.utils';
import type { SelectBulkAction } from 'src/shared/select/select.component';
import {
  SubjectManagementStatusBadgeComponent,
} from '../status-badge/subject-management-status-badge.component';

declare const bootstrap: any;

const STATIC_MODAL_OPTIONS = {
  backdrop: 'static',
  keyboard: false,
};

@Component({
  selector: 'app-teacher-management-modal',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
    SubjectManagementStatusBadgeComponent,
  ],
  templateUrl: './teacher-management-modal.component.html',
  styleUrl: './teacher-management-modal.component.scss',
})
export class TeacherManagementModalComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) teacherModalForm!: FormGroup;
  @Input({ required: true }) teacherDraftRows!: FormArray<FormGroup>;
  @Input() teacherOptions: TeacherOption[] = [];
  @Input() excludedTeacherUserIds: number[] = [];
  @Input() teacherTeachingRangeOptions: TeachingRangeOption[] = [];
  @Input() teachingTimes: TeachingTimeValue[] = [];
  @Input() workloadHoursText = '-';
  @Input() errorMessage = '';
  @Input() midtermStartDate: string | null = null;
  @Input() midtermEndDate: string | null = null;
  @Input() isSaving = false;
  @Input() editableTeacherRows: boolean[] = [];
  @Input() workloadModeEditable = true;
  @Input() teacherCollectionEditable = true;
  @Input() addTeacherEditable = true;

  @Output() addTeacher = new EventEmitter<void>();
  @Output() removeTeacher = new EventEmitter<number>();
  @Output() teacherSelected = new EventEmitter<{ rowIndex: number; teacher?: TeacherOption }>();
  @Output() saveTeachers = new EventEmitter<void>();
  @Output() cancelTeachers = new EventEmitter<void>();

  @ViewChild('teacherModal')
  private teacherModal?: ElementRef<HTMLElement>;

  private availableTeachingRangeOptionsByRow: TeachingRangeOption[][] = [];
  private availableTeacherOptionsByRow: TeacherOption[][] = [];
  private formChangesSubscription = new Subscription();

  ngOnChanges(changes: SimpleChanges): void {
    if ('teacherModalForm' in changes || 'teacherDraftRows' in changes) {
      this.subscribeToFormChanges();
    }

    this.refreshAvailableTeacherOptions();
    this.refreshAvailableTeachingRangeOptions();
  }

  ngOnDestroy(): void {
    this.formChangesSubscription.unsubscribe();
  }

  open(): void {
    const modalEl = this.teacherModal?.nativeElement;

    if (modalEl == null) {
      return;
    }

    bootstrap.Modal.getOrCreateInstance(modalEl, STATIC_MODAL_OPTIONS).show();
  }

  close(): void {
    const modalEl = this.teacherModal?.nativeElement;

    if (modalEl == null) {
      return;
    }

    this.blurFocusedElementInside(modalEl);

    bootstrap.Modal.getInstance(modalEl)?.hide();
  }

  getAvailableTeacherTeachingRangeOptions(rowIndex: number): TeachingRangeOption[] {
    return this.availableTeachingRangeOptionsByRow[rowIndex] ?? [];
  }

  getAvailableTeacherOptions(rowIndex: number): TeacherOption[] {
    return this.availableTeacherOptionsByRow[rowIndex] ?? [];
  }

  getTeachingRangeBulkActions(rowIndex: number): SelectBulkAction[] {
    const midtermStartDate = this.midtermStartDate;
    const midtermEndDate = this.midtermEndDate;
    if (!midtermStartDate || !midtermEndDate) {
      return [];
    }

    const options = this.getAvailableTeacherTeachingRangeOptions(rowIndex);
    return [
      {
        label: 'ก่อนสอบกลางภาค',
        values: this.filterTeachingRangeValues(options, (date) => date < midtermStartDate),
      },
      {
        label: 'หลังสอบกลางภาค',
        values: this.filterTeachingRangeValues(options, (date) => date > midtermEndDate),
      },
    ];
  }

   onTeacherSelected(rowIndex: number, teacher?: TeacherOption): void {
    this.teacherSelected.emit({ rowIndex, teacher });
  }

  getTeacherWorkloadHours(rowIndex: number): string {
    return calculateRegularTeacherWorkloadHours(
      this.teacherDraftRows.at(rowIndex).get('teachingRange')?.value,
      this.teachingTimes,
      this.workloadHoursText,
      this.isNotSharedWorkloadMode() ? 'notShared' : 'shared'
    );
  }

  getTeacherStatusLabel(rowIndex: number): string {
    const row = this.teacherDraftRows.at(rowIndex);

    if (this.isUnsavedTeacherRow(row)) {
      return 'แบบร่าง';
    }

    return this.displayControlText(row.get('statusLabel')?.value, 'รอดำเนินการ');
  }

  getTeacherStatusType(rowIndex: number): string {
    const row = this.teacherDraftRows.at(rowIndex);

    if (this.isUnsavedTeacherRow(row)) {
      return 'draft';
    }

    return this.displayControlText(row.get('statusType')?.value, 'pending');
  }

  isTeacherRowEditable(rowIndex: number): boolean {
    return this.isUnsavedTeacherRow(this.teacherDraftRows.at(rowIndex))
      // Missing entries are editable by default; only an explicit false locks a row.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
      || this.editableTeacherRows[rowIndex] !== false;
  }

  private refreshAvailableTeacherOptions(): void {
    const selectedUserIdsByRow = this.teacherDraftRows.controls
      .map((row) => this.normalizeTeacherUserId(row.get('userId')?.value));

    this.availableTeacherOptionsByRow = selectedUserIdsByRow.map((currentUserId, rowIndex) => {
      const selectedUserIdsByOtherRows = new Set(
        selectedUserIdsByRow.filter((userId, index): userId is string => index !== rowIndex && userId != null)
      );

      return this.teacherOptions.filter((teacher) => {
        const teacherId = this.normalizeTeacherUserId(teacher.id);

        if (teacherId == null) {
          return false;
        }

        const isExcludedByStatus = this.excludedTeacherUserIds.some(
          (userId) => this.normalizeTeacherUserId(userId) === teacherId
        );

        return teacherId === currentUserId
          || (!isExcludedByStatus && !selectedUserIdsByOtherRows.has(teacherId));
      });
    });
  }



  private isNotSharedWorkloadMode(): boolean {
    return this.teacherModalForm.get('workloadMode')?.value === 'notShared';
  }

  private getTeacherUserIdAt(rowIndex: number): string | null {
    if (rowIndex < 0 || rowIndex >= this.teacherDraftRows.length) {
      return null;
    }

    return this.normalizeTeacherUserId(this.teacherDraftRows.at(rowIndex).get('userId')?.value);
  }

  private normalizeTeacherUserId(value: unknown): string | null {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? String(value) : null;
    }

    if (typeof value !== 'string') {
      return null;
    }

    const userId = value.trim();

    return userId === '' ? null : userId;
  }

  private isUnsavedTeacherRow(row: FormGroup): boolean {
    const value = row.get('isUnsaved')?.value;

    return value == null ? true : value === true;
  }

  private displayControlText(value: unknown, fallback: string): string {
    const text = typeof value === 'string' || typeof value === 'number'
      ? String(value).trim()
      : '';

    return text === '' ? fallback : text;
  }

  private subscribeToFormChanges(): void {
    this.formChangesSubscription.unsubscribe();
    this.formChangesSubscription = new Subscription();

    this.formChangesSubscription.add(
      this.teacherDraftRows.valueChanges.subscribe(() => {
        this.refreshAvailableTeacherOptions();
        this.refreshAvailableTeachingRangeOptions();
      })
    );

    const workloadMode = this.teacherModalForm.get('workloadMode');

    if (workloadMode != null) {
      this.formChangesSubscription.add(
        workloadMode.valueChanges.subscribe(() => {
          this.refreshAvailableTeacherOptions();
          this.refreshAvailableTeachingRangeOptions();
        })
      );
    }
  }

  private refreshAvailableTeachingRangeOptions(): void {
    this.availableTeachingRangeOptionsByRow = buildAvailableTeacherTeachingRangeOptions(
      this.teacherTeachingRangeOptions,
      this.teacherDraftRows.controls.map((row) => row.get('teachingRange')?.value as unknown),
      this.isNotSharedWorkloadMode() ? 'notShared' : 'shared'
    );
  }

  private filterTeachingRangeValues(
    options: TeachingRangeOption[],
    predicate: (date: string) => boolean
  ): string[] {
    return options
      .filter((option) => {
        const parsedRange = parseTeachingRangeIsoDate(option.value);
        return parsedRange != null && predicate(parsedRange.date);
      })
      .map((option) => option.value);
  }

  private blurFocusedElementInside(modalEl: HTMLElement): void {
    const activeElement = document.activeElement;

    if (activeElement instanceof HTMLElement && modalEl.contains(activeElement)) {
      activeElement.blur();
    }
  }

}
